import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Badge,
  Box,
  Button,
  ColumnLayout,
  Container,
  Header,
  Link,
  SpaceBetween,
  StatusIndicator,
  Table,
  TextFilter,
  type TableProps,
} from '@cloudscape-design/components';
import { fetchTenants, type Tenant } from '../api/tenants';
import { fetchDeployments, type RoleDeployment } from '../api/deployments';

interface CustomerDeploymentRow {
  tenantId: string;
  tenantName: string;
  companyName: string;
  contactEmail: string;
  productId: string;
  productName: string;
  roleStatus: string;
  customerAccountId: string;
  stackName: string;
  stackVersion: string;
  customDomain: string;
  deployedAt: string;
  validated: boolean;
}

export function CustomerDeployments() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState<CustomerDeploymentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    setLoading(true);
    setError(null);
    try {
      const tenantsRes = await fetchTenants();
      const allRows: CustomerDeploymentRow[] = [];

      await Promise.all(tenantsRes.tenants.map(async (tenant) => {
        try {
          const depRes = await fetchDeployments(tenant.tenantId);
          for (const dep of depRes.deployments) {
            allRows.push({
              tenantId: tenant.tenantId,
              tenantName: tenant.name,
              companyName: tenant.companyName,
              contactEmail: tenant.contactEmail,
              productId: dep.productId,
              productName: dep.productName,
              roleStatus: dep.status,
              customerAccountId: dep.stackOutputs?.customerAccountId || tenant.awsAccountId || '',
              stackName: dep.stackOutputs?.stackName || '',
              stackVersion: dep.stackOutputs?.version || '',
              customDomain: dep.stackOutputs?.customDomain || (dep as unknown as Record<string, unknown>).customDomain as string || '',
              deployedAt: dep.stackOutputs?.deployedAt || dep.validatedAt || '',
              validated: dep.validated,
            });
          }
        } catch { /* tenant may have no deployments */ }
      }));

      // Sort by most recently deployed first
      allRows.sort((a, b) => (b.deployedAt || '').localeCompare(a.deployedAt || ''));
      setRows(allRows);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  const filtered = rows.filter((r) => {
    if (!filterText) return true;
    const s = filterText.toLowerCase();
    return r.tenantName.toLowerCase().includes(s) ||
      r.companyName.toLowerCase().includes(s) ||
      r.productName.toLowerCase().includes(s) ||
      r.customerAccountId.includes(s) ||
      r.stackName.toLowerCase().includes(s) ||
      r.customDomain.toLowerCase().includes(s);
  });

  // Summary stats
  const totalDeployments = rows.length;
  const integrated = rows.filter((r) => r.roleStatus === 'integrated').length;
  const deployed = rows.filter((r) => r.roleStatus === 'deployed').length;
  const pending = rows.filter((r) => r.roleStatus === 'pending' || r.roleStatus === 'parameters_set').length;
  const failed = rows.filter((r) => r.roleStatus === 'failed' || r.roleStatus === 'validation_failed').length;
  const withDomains = rows.filter((r) => r.customDomain).length;
  const uniqueTenants = new Set(rows.map((r) => r.tenantId)).size;

  function statusDot(status: string) {
    const map: Record<string, 'success' | 'in-progress' | 'pending' | 'error' | 'stopped'> = {
      integrated: 'success', deployed: 'success', parameters_set: 'in-progress',
      cfn_launched: 'in-progress', pending: 'pending', validation_failed: 'error',
      failed: 'error', removed: 'stopped',
    };
    return <StatusIndicator type={map[status] || 'pending'}>{status.replace(/_/g, ' ')}</StatusIndicator>;
  }

  const columns: TableProps.ColumnDefinition<CustomerDeploymentRow>[] = [
    {
      id: 'tenant', header: 'Tenant', width: 160,
      cell: (item) => (
        <Link onFollow={(e) => { e.preventDefault(); navigate(`/ops/tenants/${item.tenantId}`); }}>
          {item.tenantName}
        </Link>
      ),
    },
    { id: 'company', header: 'Company', cell: (item) => item.companyName, width: 150 },
    { id: 'product', header: 'Product', cell: (item) => item.productName, width: 130 },
    { id: 'status', header: 'Status', width: 140, cell: (item) => statusDot(item.roleStatus) },
    {
      id: 'account', header: 'AWS Account', width: 130,
      cell: (item) => item.customerAccountId ? <code style={{ fontSize: 12 }}>{item.customerAccountId}</code> : '—',
    },
    {
      id: 'stack', header: 'Stack', width: 180,
      cell: (item) => item.stackName ? <code style={{ fontSize: 11 }}>{item.stackName}</code> : '—',
    },
    {
      id: 'version', header: 'Version', width: 90,
      cell: (item) => item.stackVersion ? <Badge>{`v${item.stackVersion}`}</Badge> : '—',
    },
    {
      id: 'domain', header: 'Custom Domain', width: 200,
      cell: (item) => item.customDomain ? (
        <Link external href={item.customDomain}>{item.customDomain.replace('https://', '')}</Link>
      ) : '—',
    },
    {
      id: 'deployed', header: 'Deployed', width: 130,
      cell: (item) => item.deployedAt ? new Date(item.deployedAt).toLocaleDateString() : '—',
    },
    {
      id: 'actions', header: '', width: 100,
      cell: (item) => (
        <Button variant="inline-link" onClick={() => navigate(`/ops/deployments/${item.tenantId}`)}>
          Manage
        </Button>
      ),
    },
  ];

  return (
    <SpaceBetween size="l">
      <Header variant="h1" description="All deployed product stacks across customer accounts."
        actions={<Button iconName="refresh" onClick={loadAll} loading={loading}>Refresh</Button>}
      >Customer Deployments</Header>

      {error && <Alert type="error" dismissible onDismiss={() => setError(null)}>{error}</Alert>}

      <ColumnLayout columns={4} variant="text-grid">
        <Container>
          <Box variant="awsui-key-label">Total Deployments</Box>
          <Box variant="awsui-value-large"><span style={{ fontSize: 32, fontWeight: 700 }}>{totalDeployments}</span></Box>
          <Box color="text-status-inactive">{uniqueTenants} tenant{uniqueTenants !== 1 ? 's' : ''}</Box>
        </Container>
        <Container>
          <Box variant="awsui-key-label">Integrated</Box>
          <Box variant="awsui-value-large"><span style={{ fontSize: 32, fontWeight: 700, color: '#22c55e' }}>{integrated}</span></Box>
          <Box color="text-status-inactive">{deployed} deployed</Box>
        </Container>
        <Container>
          <Box variant="awsui-key-label">Pending / In Progress</Box>
          <Box variant="awsui-value-large"><span style={{ fontSize: 32, fontWeight: 700, color: '#3b82f6' }}>{pending}</span></Box>
          <Box color="text-status-inactive">{failed} failed</Box>
        </Container>
        <Container>
          <Box variant="awsui-key-label">Custom Domains</Box>
          <Box variant="awsui-value-large"><span style={{ fontSize: 32, fontWeight: 700 }}>{withDomains}</span></Box>
          <Box color="text-status-inactive">of {totalDeployments} deployments</Box>
        </Container>
      </ColumnLayout>

      <Table
        columnDefinitions={columns}
        items={filtered}
        loading={loading}
        loadingText="Loading deployments across all tenants..."
        trackBy={(item) => `${item.tenantId}-${item.productId}`}
        variant="full-page"
        stickyHeader
        stripedRows
        empty={
          <Box textAlign="center" padding="xxl">
            <Box variant="h3">No deployments yet</Box>
            <Box variant="p" color="text-status-inactive">
              Deploy roles and products from the onboarding flow to see them here.
            </Box>
          </Box>
        }
        filter={
          <TextFilter filteringText={filterText} filteringPlaceholder="Search by tenant, company, product, account, domain..."
            onChange={({ detail }) => setFilterText(detail.filteringText)} />
        }
        header={<Header counter={`(${filtered.length})`}>All Deployments</Header>}
      />
    </SpaceBetween>
  );
}
